<template lang="pug">
.session-sidebar
  //- Bordered card: Manage + Print/Share + Session Materials
  .session-sidebar__card
    //- Manage Your Schedule CTA
    button.session-sidebar__manage-btn(type="button" @click="openModal")
      | Manage Your Schedule

    //- Print + Share row
    .session-sidebar__actions
      button.session-sidebar__action-btn(type="button" @click="onPrint")
        svg(viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true")
          path(d="M4 4V2h8v2M4 11H3a1 1 0 01-1-1V6a1 1 0 011-1h10a1 1 0 011 1v4a1 1 0 01-1 1h-1M4 9h8v5H4z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round")
          circle(cx="11.5" cy="7" r="0.5" fill="currentColor")
        span Print
      button.session-sidebar__action-btn(type="button" ref="shareBtn" @click="openShare")
        svg(viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true")
          circle(cx="13" cy="3" r="2" stroke="currentColor" stroke-width="1.5")
          circle(cx="13" cy="13" r="2" stroke="currentColor" stroke-width="1.5")
          circle(cx="3" cy="8" r="2" stroke="currentColor" stroke-width="1.5")
          path(d="M11.5 4l-7 3M11.5 12l-7-3" stroke="currentColor" stroke-width="1.5")
        span Share

    //- Session Materials
    .session-sidebar__materials
      .session-sidebar__materials-label Session Materials
      .session-sidebar__materials-panel
        img.session-sidebar__lock-icon(src="/img/icons/schedule/sm/lock.svg" width="24" height="24" aria-hidden="true" alt="")
        p.session-sidebar__materials-copy Log in to download presentation slides and handouts.
        button.session-sidebar__login-btn(type="button" @click="openModal") Log In To Access

  //- Sponsor
  .session-sidebar__sponsor(v-if="session.sponsorName")
    .session-sidebar__sponsor-logo {{ session.sponsorName }}
</template>

<script>
import { scheduleEventBus, SCHEDULE_EVENTS } from './scheduleEventBus';

export default {
  name: 'SessionDetailSidebar',

  props: {
    session: {
      type: Object,
      required: true,
    },
  },

  methods: {
    openModal() {
      scheduleEventBus.$emit(SCHEDULE_EVENTS.OPEN_MODAL, { sessionUrl: this.session.sessionUrl || '' });
    },

    openShare() {
      scheduleEventBus.$emit(SCHEDULE_EVENTS.OPEN_SHARE, { triggerEl: this.$refs.shareBtn });
    },

    onPrint() {
      // Detail-print: render SessionPrintView for this session, not the schedule listing.
      scheduleEventBus.$emit(SCHEDULE_EVENTS.PRINT_SESSION, { session: this.session });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

.session-sidebar {
  width: 368px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 1023px) {
    width: 100%;
    max-width: none;
  }
}

.session-sidebar__card {
  background: white;
  border: 1px solid $color-border;
  border-radius: 16px;
  padding: 36px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.session-sidebar__manage-btn {
  width: 100%;
  height: 48px;
  background: $color-cta-accent;
  color: $color-cta-text;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  box-shadow: $shadow-btn-subtle;

  &:hover {
    background: $color-cta-accent-hover;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}

.session-sidebar__actions {
  display: flex;
  gap: 16px;
}

.session-sidebar__action-btn {
  flex: 1;
  height: 40px;
  padding: 0 12px;
  background: white;
  border: 1px solid $color-primary;
  border-radius: 8px;
  color: $color-text-dark;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-shadow: $shadow-btn;

  &:hover {
    background: $color-btn-outline-hover-bg;
    border-color: $color-btn-outline-hover-border;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}

.session-sidebar__materials {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.session-sidebar__materials-label {
  font-size: 13px;
  font-weight: 600;
  color: $color-text-secondary;
  line-height: 1;
  padding-top: 50px;
}

.session-sidebar__materials-panel {
  border: 1px dashed $color-border;
  background: $color-surface-sunken;
  border-radius: 8px;
  padding: 16px 21px 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.session-sidebar__lock-icon {
  flex-shrink: 0;
}

.session-sidebar__materials-copy {
  font-size: 16px;
  font-weight: 400;
  color: $color-text-secondary;
  line-height: 1.5;
  margin: 0;
  text-align: left;
  width: 100%;
}

.session-sidebar__login-btn {
  height: 40px;
  padding: 0 12px;
  background: white;
  border: 1px solid $color-primary;
  border-radius: 8px;
  color: $color-text-dark;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  box-shadow: $shadow-btn;

  &:hover {
    background: $color-btn-outline-hover-bg;
    border-color: $color-btn-outline-hover-border;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}

.session-sidebar__sponsor {
  background: white;
  border: 1px solid $color-border;
  border-radius: 16px;
  padding: 72px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.session-sidebar__sponsor-label {
  font-size: 12px;
  font-weight: 600;
  color: $color-text-secondary;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.session-sidebar__sponsor-logo {
  width: 176px;
  font-size: 14px;
  font-weight: 400;
  color: $color-tag-sponsor-fg;
  background: $color-tag-sponsor-bg;
  border: 1px solid $color-border;
  border-radius: 8px;
  padding: 12px 24px;
  text-align: center;
}
</style>
